/** Root DataView context provider and lightweight layout helpers. */
import { createContext, useContext, type ReactNode } from 'react';
import type { DataViewColumn, SortDirection } from './types';
import type { ViewMode } from '../ViewToggle';
import { cn } from '@/foundation/utils/cn';

// ─── Context ────────────────────────────────────────────────────────────────

interface DataViewContextValue {
  items: unknown[];
  columns: DataViewColumn<unknown>[];
  visibleColumns: Set<string>;
  onVisibleColumnsChange?: (columns: Set<string>) => void;
  viewMode: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  sortBy?: string;
  sortDirection: SortDirection;
  onSort?: (field: string, direction: SortDirection) => void;
  onItemClick?: (item: unknown) => void;
  getItemKey: (item: unknown) => string;
  getItemTextValue?: (item: unknown) => string;
  isLoading?: boolean;
  isStale?: boolean;
}

const DataViewContext = createContext<DataViewContextValue | null>(null);

export function useDataView<T = unknown>() {
  const ctx = useContext(DataViewContext);
  if (!ctx) throw new Error('useDataView must be used within <DataView>');
  return ctx as unknown as DataViewContextTyped<T>;
}

/** Typed version of context for consumer hooks. */
interface DataViewContextTyped<T> {
  items: T[];
  columns: DataViewColumn<T>[];
  visibleColumns: Set<string>;
  onVisibleColumnsChange?: (columns: Set<string>) => void;
  viewMode: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  sortBy?: string;
  sortDirection: SortDirection;
  onSort?: (field: string, direction: SortDirection) => void;
  onItemClick?: (item: T) => void;
  getItemKey: (item: T) => string;
  getItemTextValue?: (item: T) => string;
  isLoading?: boolean;
  isStale?: boolean;
}

// ─── Root Provider ──────────────────────────────────────────────────────────

interface DataViewProps<T> {
  items: T[];
  columns: DataViewColumn<T>[];
  visibleColumns: Set<string>;
  onVisibleColumnsChange?: (columns: Set<string>) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  sortBy?: string;
  sortDirection?: SortDirection;
  onSort?: (field: string, direction: SortDirection) => void;
  onItemClick?: (item: T) => void;
  getItemKey: (item: T) => string;
  getItemTextValue?: (item: T) => string;
  isLoading?: boolean;
  isStale?: boolean;
  children: ReactNode;
  className?: string;
}

export function DataView<T>({
  children,
  className,
  items,
  columns,
  visibleColumns,
  onVisibleColumnsChange,
  viewMode = 'table',
  onViewModeChange,
  sortBy,
  sortDirection = 'desc',
  onSort,
  onItemClick,
  getItemKey,
  getItemTextValue,
  isLoading,
  isStale,
}: DataViewProps<T>) {
  const value: DataViewContextValue = {
    items: items as unknown[],
    columns: columns as DataViewColumn<unknown>[],
    visibleColumns,
    onVisibleColumnsChange,
    viewMode,
    onViewModeChange,
    sortBy,
    sortDirection,
    onSort,
    onItemClick: onItemClick as ((item: unknown) => void) | undefined,
    getItemKey: getItemKey as (item: unknown) => string,
    getItemTextValue: getItemTextValue as ((item: unknown) => string) | undefined,
    isLoading,
    isStale,
  };

  return (
    <DataViewContext.Provider value={value}>
      <div className={cn('flex flex-col', className)}>{children}</div>
    </DataViewContext.Provider>
  );
}

// ─── Toolbar ────────────────────────────────────────────────────────────────

/** Flex wrapper for toolbar items (search, filters, toggles, etc.). */
export function DataViewToolbar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('flex flex-wrap items-center gap-2', className)}>{children}</div>;
}

// ─── View Toggle ────────────────────────────────────────────────────────────

export { DataViewViewToggle } from './DataViewViewToggle';
